/**
 * Privacy Policy page.
 *
 * DRAFT: this is a starting-point template based on what this site actually
 * does (collecting contact details for orders, taking payment via Square,
 * emailing invoices, cookieless analytics via Umami) and on the rights GDPR
 * requires you to describe. It has not been reviewed by a lawyer — GDPR
 * carries real regulatory penalties, so have this checked before relying on
 * it as your published policy.
 *
 * @module components/PrivacyPolicy/PrivacyPolicy
 */

import React from 'react';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';

const LAST_UPDATED = 'September 27, 2026';

/**
 * PrivacyPolicy component.
 * @function PrivacyPolicy
 * @returns {string} Markup of the component.
 */
const PrivacyPolicy = () => (
  <div className="view-padding">
    <Helmet>
      <title>Privacy Policy</title>
    </Helmet>
    <article id="privacy-policy">
      <h1 className="content-title">Privacy Policy</h1>
      <p>Last updated: {LAST_UPDATED}</p>

      <p>
        Pro Series Tools LLC ("Pro Series Tools", "we", "us") respects your
        privacy. This page explains what information we collect when you visit
        our website or place an order with us, how we use it, who we share it
        with, and the rights you have over it, including the rights granted by
        the EU/UK General Data Protection Regulation (GDPR).
      </p>

      <h2>Information You Provide</h2>
      <p>
        When you place an order, we collect the information needed to fulfill
        it: your name, company name, email address, phone number, and shipping
        address. If you contact us directly, we keep whatever information you
        send us so we can respond.
      </p>

      <h2>Information Collected Automatically</h2>
      <p>
        We use <strong>Umami</strong>, a privacy-focused, cookieless website
        analytics tool, to understand overall traffic to our site. We do not use
        tracking cookies, and Umami does not track you across other websites. It
        collects only aggregated, anonymized technical information — such as
        which pages were viewed, the referring website, browser and device type,
        and country-level location — and cannot be used to identify you
        personally.
      </p>

      <h2>Cookies</h2>
      <p>
        We do not set tracking or advertising cookies. Any cookies used are
        strictly limited to what's needed for the site to function (for example,
        keeping you logged in to an account area), never for tracking you across
        sites or building an advertising profile.
      </p>

      <h2>How We Use Your Information</h2>
      <p>
        We use your information to process and ship your order, send you an
        invoice and order updates by email, respond to questions you send us,
        and understand overall site usage so we can improve it. We do not sell
        your information, and we do not use it for advertising.
      </p>

      <h2>Legal Basis for Processing (GDPR)</h2>
      <p>If you are located in the EEA or UK, our legal bases are:</p>
      <ul>
        <li>
          <strong>Contract:</strong> processing your order details, contact
          information, and payment is necessary to fulfill our contract with
          you.
        </li>
        <li>
          <strong>Legitimate interest:</strong> using anonymized analytics to
          understand and improve our website, and responding to enquiries you
          send us.
        </li>
        <li>
          <strong>Legal obligation:</strong> keeping order records for
          bookkeeping and tax purposes.
        </li>
      </ul>

      <h2>Payment Processing</h2>
      <p>
        Payments are processed by Square, acting as an independent data
        processor. We do not store your card details ourselves; they are handled
        directly by Square under{' '}
        <a
          href="https://squareup.com/us/en/legal/general/privacy"
          target="_blank"
          rel="noopener noreferrer"
        >
          Square's own privacy policy
        </a>
        .
      </p>

      <h2>Who We Share Your Information With</h2>
      <p>
        We share information only with the service providers we use to run our
        business — currently Square for payment processing and Umami for
        analytics — and only to the extent needed for them to perform that
        service. We do not sell your information or share it for advertising
        purposes.
      </p>

      <h2>Data Retention</h2>
      <p>
        We keep order and contact records for as long as needed for bookkeeping,
        warranty, and customer-service purposes, and as required by law.
        Aggregated analytics data does not identify you and is not tied to your
        order or contact information.
      </p>

      <h2>Your Rights Under GDPR</h2>
      <p>If you are located in the EEA or UK, you have the right to:</p>
      <ul>
        <li>
          Ask us what personal data we hold about you, and see a copy of it
          (access).
        </li>
        <li>
          Ask us to correct inaccurate or incomplete data (rectification).
        </li>
        <li>
          Ask us to delete your data, where we're not required to keep it for
          legal or bookkeeping reasons (erasure).
        </li>
        <li>Ask us to limit how we use your data (restriction).</li>
        <li>Receive your data in a portable format (data portability).</li>
        <li>
          Object to our use of your data for legitimate-interest purposes,
          including analytics.
        </li>
      </ul>
      <p>
        To exercise any of these rights, contact us using the details below. If
        you're not satisfied with our response, you also have the right to lodge
        a complaint with your local data protection supervisory authority.
      </p>

      <h2>Changes to This Policy</h2>
      <p>
        We may update this policy from time to time. Changes will be posted on
        this page with an updated "Last updated" date.
      </p>

      <h2>Data Controller &amp; Contact Us</h2>
      <p>
        Pro Series Tools LLC is the data controller for the information
        described in this policy.
        <br />
        <br />
        Pro Series Tools LLC
        <br />
        36 Road 5243
        <br />
        Farmington, NM 87401
        <br />
        United States
        <br />
        <a href="tel:+15056327039">+1 (505) 632-7039</a>
        <br />
        <a href="mailto:info@proseriesleathertools.com">
          info@proseriesleathertools.com
        </a>
      </p>
    </article>
  </div>
);

export default PrivacyPolicy;
